import { useState, useEffect, useRef } from "react"

export function usePersistedState<T>(
  key: string | null,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // wasNullAtMountRef: true when key was null at mount — initializer couldn't load from storage
  const wasNullAtMountRef = useRef(!key)
  // prevKeyRef: tracks the key from the previous effect run to detect key changes
  const prevKeyRef = useRef<string | null>(key)

  const [state, setState] = useState<T>(() => {
    if (!key) return defaultValue
    try {
      const stored = localStorage.getItem(key)
      if (stored !== null) return JSON.parse(stored) as T
    } catch {
      // ignore corrupt JSON or storage errors
    }
    return defaultValue
  })

  // Single effect handles both load-on-key-change and persist-on-state-change.
  // When the key changes (including null→non-null on token arrival), read from storage
  // rather than write — avoids overwriting stored data with the in-memory default.
  useEffect(() => {
    if (!key) return
    const keyChanged = prevKeyRef.current !== key
    if (keyChanged || wasNullAtMountRef.current) {
      prevKeyRef.current = key
      wasNullAtMountRef.current = false
      try {
        const stored = localStorage.getItem(key)
        if (stored !== null) {
          setState(JSON.parse(stored) as T)
        }
      } catch {
        // ignore corrupt JSON or storage errors
      }
    } else {
      try {
        localStorage.setItem(key, JSON.stringify(state))
      } catch {
        // ignore storage full or other errors
      }
    }
  }, [key, state])

  return [state, setState]
}
